// Detección de plataforma. Usa Capacitor cuando la app corre dentro del shell
// nativo (APK Android / IPA iOS). En navegador devuelve 'web'.
//
// Por qué importa: la App Store de Apple (Guideline 3.1.1) prohíbe vender
// suscripciones digitales con cualquier método que no sea su IAP. Tenemos que
// ocultar el panel de facturación (Stripe) cuando la app corre dentro del
// contenedor iOS. La web sigue mostrando todo normal.
//
// El binario iOS aprobado por Apple es un WKWebView que carga app.ikognit.com — pero
// debemos comportarnos diferente DENTRO de ese WebView para no infringir 3.1.1.

import { Capacitor } from '@capacitor/core';

export function getPlatform(): 'ios' | 'android' | 'web' {
  // Debug: ?force_mobile=ios|android para inspeccionar layout móvil desde
  // un navegador normal o headless (sin Capacitor). Solo activa si está
  // el query param explícitamente.
  try {
    const q = new URL(window.location.href).searchParams;
    const force = q.get('force_mobile');
    if (force === 'ios') return 'ios';
    if (force === 'android') return 'android';
    // Debug: ?force_tablet=ios|android simula el shell nativo de un TABLET
    // (iPad) desde un navegador sin Capacitor. Reproduce el estado exacto del
    // rechazo de Apple 2026-07-07 (Guideline 4) para poder verificarlo headless.
    const ft = q.get('force_tablet');
    if (ft === 'ios') return 'ios';
    if (ft === 'android') return 'android';
  } catch { /* ignore */ }
  try {
    if (Capacitor.isNativePlatform()) {
      const p = Capacitor.getPlatform();
      if (p === 'ios') return 'ios';
      if (p === 'android') return 'android';
    }
  } catch { /* en SSR o entornos raros — caemos a 'web' */ }
  return 'web';
}

export function isIOS(): boolean { return getPlatform() === 'ios'; }
export function isAndroid(): boolean { return getPlatform() === 'android'; }
export function isWeb(): boolean { return getPlatform() === 'web'; }

// Tablet (iPad / tablet Android grande): es plataforma nativa PERO con pantalla
// ancha. En ese caso usamos la vista de 2 paneles (Chat.web), como hacen
// WhatsApp/Telegram/Signal en iPad — no la vista móvil de 1 columna. El umbral
// de 700px en el lado corto separa con holgura: ningún iPhone llega (máx ~430),
// todo iPad sí (mín 768). `getPlatform()` sigue devolviendo 'ios' para que
// shouldHideBilling() y el resto de lógica iOS no cambien.
export function isTablet(): boolean {
  try {
    const q = new URL(window.location.href).searchParams;
    const force = q.get('force_mobile');
    if (force === 'ios' || force === 'android') return false; // debug: fuerza móvil
    const ft = q.get('force_tablet');
    if (ft === 'ios' || ft === 'android') return true; // debug: fuerza tablet
  } catch { /* ignore */ }
  if (getPlatform() === 'web') return false;
  try {
    // OJO (rechazo Apple 2026-07-13, "error al entrar" SOLO en iPad): main.tsx llama
    // a isTablet() al ARRANCAR, y en el WKWebView nativo `window.innerWidth/Height`
    // pueden valer 0 todavía (la webview aún no ha hecho layout). Con 0 esto devolvía
    // FALSE en un iPad → el <body> se marcaba como móvil, pero Chat.tsx (que re-evalúa
    // más tarde, ya con medidas buenas) montaba Chat.web, cuyo CSS está scoped a
    // `.platform-web` → la app aparecía ROTA/sin estilos justo al entrar. En iPhone el
    // FALSE es correcto de todas formas, por eso allí nunca se vio.
    // Fallback a las medidas de PANTALLA, que en WKWebView sí están disponibles ya.
    let w = window.innerWidth || 0;
    let h = window.innerHeight || 0;
    if (!w || !h) {
      w = (window.screen && window.screen.width) || 0;
      h = (window.screen && window.screen.height) || 0;
    }
    if (!w || !h) return false;
    return Math.min(w, h) >= 700;
  } catch { return false; }
}

// Apple Guideline 3.1.1 — ocultar billing/Stripe dentro del WebView iOS.
export function shouldHideBilling(): boolean { return isIOS(); }
